"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
}

export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
}: ConfirmationDialogProps) {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card border border-border/40 shadow-2xl">
        <DialogHeader className="pb-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div
              className={`p-1.5 rounded-md ${
                variant === "destructive" ? "bg-red-500/10" : "bg-primary/10"
              }`}
            >
              <AlertTriangle
                className={`h-4 w-4 ${
                  variant === "destructive" ? "text-red-600" : "text-primary"
                }`}
              />
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground">
              {title}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="py-6">
          <div
            className={`bg-muted/30 rounded-lg border border-border/40 p-4 ${
              variant === "destructive" ? "border-red-200/60 bg-red-50/30" : ""
            }`}
          >
            <p className="text-sm text-foreground leading-relaxed">
              {description}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border/60">
          <Button
            variant="outline"
            onClick={onClose}
            className="text-sm h-8 px-4 border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 dark:border-red-800 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900 dark:hover:border-red-700 rounded-lg"
          >
            {cancelText}
          </Button>
          <Button
            onClick={handleConfirm}
            className={`text-sm h-8 px-4 shadow-md rounded-lg ${
              variant === "destructive"
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-primary text-primary-foreground hover:bg-primary/90"
            }`}
          >
            {confirmText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
